import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import "bootstrap/dist/css/bootstrap.min.css";
import { Edit, LocationOn, Delete, Add, ArrowBack, Info } from "@mui/icons-material";
import {
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  Typography,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  Tooltip,
  InputAdornment,
} from "@mui/material";

const WorkLocationPage = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [workLocations, setWorkLocations] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({
    locationId: "",
    locationName: "",
    address: "",
    radius: 50,
  });
  const [editingLocation, setEditingLocation] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogContent, setDialogContent] = useState("");

  const locationsPerPage = 10;
  const totalPages = Math.ceil(workLocations.length / locationsPerPage);
  const displayedLocations = workLocations.slice(
    (currentPage - 1) * locationsPerPage,
    currentPage * locationsPerPage
  );

  // Base URL for your work location API endpoints
  const BASE_API_URL = "https://attendanceapi.gensparc.com/work";

  // Fetch work locations from backend API
  const fetchWorkLocations = async () => {
    try {
      const token = localStorage.getItem("token");
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(`${BASE_API_URL}/worklist`, config);
      setWorkLocations(response.data);
    } catch (error) {
      console.error("Error fetching work locations:", error);
    }
  };

  useEffect(() => {
    fetchWorkLocations();
  }, []);

  // Reset current page if needed
  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(1);
    }
  }, [workLocations, totalPages, currentPage]);

  const handlePageChange = (page) => setCurrentPage(page);

  const handleSelectLocation = useCallback((locationId) => {
    setSelectedLocations((prev) =>
      prev.includes(locationId)
        ? prev.filter((id) => id !== locationId)
        : [...prev, locationId]
    );
  }, []);

  const handleDelete = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const config = { headers: { Authorization: `Bearer ${token}` } };

      // Delete each selected location by calling the DELETE endpoint
      for (const id of selectedLocations) {
        await axios.delete(`${BASE_API_URL}/workdelete/${id}`, config);
      }
      // Refresh the work locations list after deletion
      fetchWorkLocations();
      setSelectedLocations([]);
    } catch (error) {
      console.error("Error deleting work location(s):", error);
    }
  }, [selectedLocations]);

  const handleAddLocationClick = (location = null) => {
    setFormData({
      locationId: location ? location.id : "",
      locationName: location ? location.location || location.name : "",
      address: location ? location.address : "",
      radius: location ? location.radius : 50,
    });
    setEditingLocation(location);
    setShowAddForm(true);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem("token");
      const config = { headers: { Authorization: `Bearer ${token}` } };

      if (editingLocation) {
        // Update existing work location using PUT
        await axios.put(
          `${BASE_API_URL}/workupdate/${editingLocation.id}`,
          {
            location: formData.locationName,
            address: formData.address,
            radius: formData.radius,
          },
          config
        );
      } else {
        // Create new work location using POST
        await axios.post(
          `${BASE_API_URL}/workadd`,
          {
            location: formData.locationName,
            address: formData.address,
            radius: formData.radius,
          },
          config
        );
      }
      // Refresh list after add/update
      fetchWorkLocations();
      setShowAddForm(false);
      setEditingLocation(null);
    } catch (error) {
      console.error("Error submitting work location:", error);
    }
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const commonStyles = {
    fontWeight: "bold",
    fontSize: "12px",
    whiteSpace: "nowrap",
    padding: "8px",
    textAlign: "center",
  };

  // for info tooltip in table cells
    const renderTableCell = (content) => {
    if (!content) return "";
  
    const stringContent = content.toString();
    const isLong = stringContent.length > 20;
  
    return isLong ? (
      <Tooltip title={stringContent} arrow>
        <span style={{ display: "inline-flex", alignItems: "center", cursor: "default" }}>
          {stringContent.substring(0, 20)}...
          <Info fontSize="small" style={{ marginLeft: 4, color: "#888" }} />
        </span>
      </Tooltip>
    ) : (
      stringContent
    );
  };
  

  const handleCellClick = (content) => {
    setDialogContent(content);
    setOpenDialog(true);
  };

  return (
    <div style={{ marginRight: "0", paddingLeft: "5px", paddingRight: "5px" }}>
      <div className="container-fluid px-0 py-5" style={{ marginRight: "0" }}>
        <Card sx={{ padding: 3, marginBottom: 3, boxShadow: 3 }}>
          <div className="d-flex justify-content-between align-items-center">
            <Typography variant="h4" component="h1" color="textPrimary" sx={{ fontWeight: 600 }}>
              Work Location Management
            </Typography>
            <div>
              <Button
                onClick={() => handleAddLocationClick()}
                variant="contained"
                color="primary"
                startIcon={<Add />}
                sx={{
                  paddingX: 2,
                  paddingY: 1,
                  fontSize: "12px",
                  borderRadius: "15px",
                  boxShadow: 2,
                  "&:hover": { boxShadow: 4 },
                  marginRight: 1,
                }}
              >
                Add
              </Button>
              <Button
                onClick={handleDelete}
                variant="contained"
                color="error"
                startIcon={<Delete />}
                disabled={selectedLocations.length === 0}
                sx={{
                  paddingX: 2,
                  paddingY: 1,
                  fontSize: "12px",
                  borderRadius: "15px",
                  boxShadow: 2,
                  "&:hover": { boxShadow: 4 },
                  marginRight: 1,
                }}
              >
                Delete
              </Button>
            </div>
          </div>
        </Card>

        <TableContainer component={Paper} sx={{ boxShadow: 2 }}>
          <Table>
            <TableHead sx={{ backgroundColor: "#f4f5f7" }}>
              <TableRow>
                <TableCell sx={commonStyles}>
                  <input
                    type="checkbox"
                    onChange={(e) => {
                      setSelectedLocations(e.target.checked ? workLocations.map((loc) => loc.id) : []);
                    }}
                    checked={
                      selectedLocations.length > 0 &&
                      selectedLocations.length === workLocations.length
                    }
                  />
                </TableCell>
                {["Work Location ID", "Work Location Name", "Work Location Address", "Modified Time", "Radius", "Action"].map((heading, index) => (
                  <TableCell key={index} sx={commonStyles}>
                    {heading}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {displayedLocations.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} sx={commonStyles}>
                    No work locations found
                  </TableCell>
                </TableRow>
              ) : (
                displayedLocations.map((location) => (
                  <TableRow key={location.id}>
                    <TableCell sx={commonStyles}>
                      <input
                        type="checkbox"
                        checked={selectedLocations.includes(location.id)}
                        onChange={() => handleSelectLocation(location.id)}
                      />
                    </TableCell>
                    <TableCell sx={commonStyles}>{renderTableCell(location.id.toString())}</TableCell>
                    <TableCell sx={commonStyles}>{renderTableCell(location.location || location.name)}</TableCell>
                    <TableCell sx={commonStyles}>{renderTableCell(location.address)}</TableCell>
                    <TableCell sx={commonStyles}>{renderTableCell(location.modifiedTime)}</TableCell>
                    <TableCell sx={commonStyles}>{renderTableCell(location.radius.toString())}</TableCell>
                    <TableCell sx={commonStyles}>
                      <div className="d-flex justify-content-center align-items-center">
                        <IconButton
                          sx={{
                            backgroundColor: "#ffeb3b",
                            borderRadius: "10px",
                            "&:hover": { backgroundColor: "#fbc02d" },
                          }}
                          onClick={() => handleAddLocationClick(location)}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                        {/* <IconButton
                          sx={{
                            backgroundColor: "#00bcd4",
                            borderRadius: "10px",
                            "&:hover": { backgroundColor: "#008c9e" },
                            marginLeft: 1,
                          }}
                          aria-label="Locate Work Location"
                        >
                          <LocationOn fontSize="small" />
                        </IconButton> */}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <div className="d-flex justify-content-between align-items-center mt-4">
          <Typography variant="body1">
            Page {currentPage} of {totalPages}
          </Typography>
          <div>
            <Button
              variant="outlined"
              color="primary"
              disabled={currentPage === 1}
              onClick={() => handlePageChange(currentPage - 1)}
              sx={{
                marginRight: 1,
                borderRadius: "20px",
                paddingX: 1,
                paddingY: 0.5,
                fontSize: "12px",
              }}
              aria-label="Previous Page"
            >
              Previous
            </Button>
            <Button
              variant="outlined"
              color="primary"
              disabled={currentPage === totalPages}
              onClick={() => handlePageChange(currentPage + 1)}
              sx={{
                borderRadius: "20px",
                paddingX: 1,
                paddingY: 0.5,
                fontSize: "12px",
              }}
              aria-label="Next Page"
            >
              Next
            </Button>
          </div>
        </div>
      </div>

      {/* Dialog for Full Content */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} fullWidth maxWidth="md">
        <DialogTitle>Full Content</DialogTitle>
        <DialogContent>
          <p>{dialogContent}</p>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Add/Edit Work Location Dialog */}
      <Dialog open={showAddForm} onClose={() => setShowAddForm(false)} fullWidth maxWidth="md">
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <IconButton onClick={() => setShowAddForm(false)}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h6">{editingLocation ? "Edit Work Location" : "Add Work Location"}</Typography>
        </DialogTitle>

        <Box component="form" onSubmit={handleFormSubmit} sx={{ p: 3 }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "20px" }}>
            {/* Left Column */}
            <div>
              {/* <TextField
                fullWidth
                label="Work Location ID*"
                name="locationId"
                value={formData.locationId}
                onChange={handleInputChange}
                margin="normal"
                required
              /> */}
              <TextField
                fullWidth
                label="Work Location Name*"
                name="locationName"
                value={formData.locationName}
                onChange={handleInputChange}
                margin="normal"
                required
              />
              <TextField
                fullWidth
                label="Search Address"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                margin="normal"
              />
            </div>

            {/* Right Column */}
            <div>
              <TextField
                fullWidth
                label="Work Location Address*"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                margin="normal"
                required
              />
              <TextField
                fullWidth
                label="Fencing Radius (m)"
                name="radius"
                type="number"
                value={formData.radius}
                onChange={handleInputChange}
                margin="normal"
                inputProps={{ min: 50, max: 5000 }}
                required
                InputProps={{
                  startAdornment: <InputAdornment position="start">m</InputAdornment>,
                }}
              />
            </div>
          </div>

          <DialogActions sx={{ mt: 3 }}>
            <Button variant="outlined" onClick={() => setShowAddForm(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" color="primary">
              {editingLocation ? "Update" : "Submit"}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </div>
  );
};

export default WorkLocationPage;
